import { create } from 'zustand';
import type {
  LimitErrorCode,
  LimitErrorMeta,
  PaywallPayload,
} from '@/shared/lib/api';
import type { PaywallPayloadKey } from '@/types/subscription';

/**
 * A code that can drive this paywall: either a 422 limit error code, or a
 * preflight-only payload key (e.g. `PRO_FEATURE_UNAVAILABLE`, the advanced
 * render-controls chooser opened on a locked-knob tap with no 422 round-trip).
 */
export type LimitPaywallCode = LimitErrorCode | PaywallPayloadKey;

/**
 * Holds the currently visible server-driven limit paywall. The dialog is
 * mounted once at the root; feature code just calls `showPaywall(payload, code)`
 * and the shared `<LimitPaywallDialog />` picks it up.
 *
 * `code` is kept alongside the payload so consumers (e.g. analytics) can tell
 * which limit triggered it without re-parsing the error.
 *
 * `meta` is the limit error's structured metadata (when routed from a 422) —
 * the pack paywalls use `meta.project_id` to bind the offer to the building
 * the limit actually fired on, which can differ from the current route during
 * cross-building flows. Optional: preflight-routed paywalls have no meta and
 * fall back to the route's project.
 *
 * `packOnly` is the payload-FREE entry: open the Facade Kit body directly with
 * no server payload at all. Mirrors Apple's `LimitPaywallPresenter.showPack`
 * and Android's `PackPaywallController.show(Pack(payload = null))`, and exists
 * for the same reason: a locked render knob must open SOMETHING even when the
 * cached plan snapshot hasn't loaded yet. The body falls back to the
 * `GET /store` catalog copy, so it is still fully server-driven.
 */
interface LimitPaywallState {
  code: LimitPaywallCode | null;
  payload: PaywallPayload | null;
  meta: LimitErrorMeta | null;
  packOnly: boolean;
  showPaywall: (
    payload: PaywallPayload,
    code: LimitPaywallCode,
    meta?: LimitErrorMeta
  ) => void;
  /**
   * Open the Facade Kit body with no payload. `projectId` binds the offer to a
   * building; omit it and the dialog falls back to the current route's project
   * (which is where a locked knob always lives), exactly like a preflight
   * `showPaywall` call with no meta.
   */
  showPackPaywall: (projectId?: string) => void;
  hidePaywall: () => void;
}

export const useLimitPaywallStore = create<LimitPaywallState>((set) => ({
  code: null,
  payload: null,
  meta: null,
  packOnly: false,
  showPaywall: (payload, code, meta) =>
    set({ payload, code, meta: meta ?? null, packOnly: false }),
  showPackPaywall: (projectId) =>
    set({
      payload: null,
      code: 'PACK_REQUIRED',
      meta: projectId ? { project_id: projectId } : null,
      packOnly: true,
    }),
  hidePaywall: () =>
    set({ payload: null, code: null, meta: null, packOnly: false }),
}));
